// Translates between the Vuex filter state and the URL, so a copied/bookmarked
// link reopens the same filtered dashboard view. Restoring goes through the
// store's composite actions (see src/store/actions.js) so the mutual-exclusion
// rules between team/superteam/individual stay in one place, not reimplemented
// here.
//
// Two URL forms carry a team/superteam scope: the slug path this module writes,
// resolved by Home.restoreScopeFromSlugs, and the id + name query params below,
// which remain readable for older links.

// Builds the location ({ path, query }) mirroring the current filter state.
//
// A team / superteam scope is expressed as a slug path — /temasek-polytechnic,
// /org/green-school-network, or /org/<parent>/<child> when drilled — so a
// client-facing link carries no ids and no display names, and survives a rename.
// See docs/references/filtering.md.
//
// The id + name query params are the fallback for a scope whose slug we don't
// have (a leaderboard/search row the backend returned without one): the name
// still has to travel in the URL there, because nothing resolves an id back to a
// name on a cold load.
//
// An individual scope is `?user=<id>` alone — users have no slug, but their name
// comes back with the strides the filter fetches anyway (see
// Home.applyStrides), so it never has to go in the URL. It shouldn't: a strider's
// name is PII, and a URL reaches analytics, browser history and Referer headers.
//
// Keys are omitted entirely when their value is null/empty, so an unfiltered
// view is "/" with no query string at all.
export function buildFilterLocation({
  startDay,
  endDay,
  country,
  team,
  teamName,
  teamSlug,
  superteam,
  superteamName,
  superteamSlug,
  userId,
} = {}) {
  const superteamInPath = Boolean(superteam && superteamSlug);
  // A child team only goes in the path under its parent — a lone /<team-slug>
  // segment reads as a standalone team scope, which is a different filter. So
  // when the parent fell back to a query param, the child follows it there.
  const teamInPath = Boolean(
    team && teamSlug && (!superteam || superteamInPath)
  );

  let path = "/";
  if (superteamInPath) {
    path = `/org/${encodeURIComponent(superteamSlug)}`;
    if (teamInPath) path += `/${encodeURIComponent(teamSlug)}`;
  } else if (teamInPath) {
    path = `/${encodeURIComponent(teamSlug)}`;
  }

  const query = {};
  if (startDay) query.start = startDay;
  if (endDay) query.end = endDay;
  if (country) query.country = country;
  if (team && !teamInPath) {
    query.team = String(team);
    if (teamName) query.teamName = teamName;
  }
  if (superteam && !superteamInPath) {
    query.superteam = String(superteam);
    if (superteamName) query.superteamName = superteamName;
  }
  if (userId) query.user = String(userId);
  return { path, query };
}

// Dispatches the store actions needed to reproduce a URL query's filters. Dates
// and country apply to every link; the team/superteam/user id params here are the
// pre-slug form (and the no-slug fallback) — a slug path is handled by the caller
// instead, since it needs an async lookup.
// `actions` is `{ selectUser, selectMember, selectSuperteam, selectTeam,
// drillTeam, updateStartDay, updateEndDay, updateCountry }` — the same payload
// shapes as the matching Vuex actions.
export function restoreFiltersFromQuery(query, actions) {
  const {
    selectUser,
    selectMember,
    selectSuperteam,
    selectTeam,
    drillTeam,
    updateStartDay,
    updateEndDay,
    updateCountry,
  } = actions;
  const {
    start,
    end,
    country,
    team,
    teamName,
    superteam,
    superteamName,
    user,
    userName,
  } = query || {};

  const userId = toId(user);
  const teamId = toId(team);
  const superteamId = toScopeId(superteam);

  let scoped = false;

  // The team side goes first: an individual can be scoped INSIDE a team, and
  // the team actions clear any individual, so applying them second would drop it.
  if (superteamId != null && superteamName) {
    selectSuperteam({ id: superteamId, name: superteamName });
    if (teamId != null && teamName) {
      drillTeam({ id: teamId, name: teamName });
    }
    scoped = true;
  } else if (teamId != null && teamName) {
    selectTeam({ id: teamId, name: teamName });
    scoped = true;
  }

  // An individual restores from the id alone: the name is filled in from the
  // strides response (Home.applyStrides), so it isn't in the URL any more. A
  // `userName` from an older link is still honoured — it saves the panel showing
  // the generic "Individual" label until the first fetch lands. Alongside a
  // restored team it's a member selection (keep that team); on its own it's the
  // dashboard-wide individual scope.
  if (userId != null) {
    const payload = { id: userId, name: userName || null };
    if (scoped) selectMember(payload);
    else selectUser(payload);
  }

  // Country and date apply whatever else the link scoped, matching the store: no
  // composite scope action touches either, so a country picked alongside a team
  // survives in the app and has to survive the round trip too.
  if (start) updateStartDay(start);
  if (end) updateEndDay(end);
  if (country) updateCountry(country);
}

function toId(value) {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// Superteam ids aren't guaranteed numeric — the backend may use an opaque string.
// Numeric ones are still converted, so the restored id compares equal (===) to the
// one on a leaderboard row; anything else passes through as the string it is. An
// empty value is no id at all.
function toScopeId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : String(value);
}
